import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface LanguageContextType {
  language: 'fr' | 'en';
  setLanguage: (lang: 'fr' | 'en') => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const translations = {
  fr: {
    // Login page
    'login.title': 'Connexion à Hook Lab',
    'login.subtitle': 'Accédez à votre tableau de bord de prospection',
    'login.email': 'Adresse email',
    'login.password': 'Mot de passe',
    'login.rememberMe': 'Se souvenir de moi',
    'login.forgotPassword': 'Mot de passe oublié ?',
    'login.signIn': 'Se connecter',
    'login.signingIn': 'Connexion en cours...',
    'login.invalidCredentials': 'Email ou mot de passe incorrect',
    'login.emailRequired': 'L\'email est requis',
    'login.passwordRequired': 'Le mot de passe est requis',
    'login.invalidEmail': 'Veuillez entrer une adresse email valide',
    'login.passwordMinLength': 'Le mot de passe doit contenir au moins 6 caractères',
    
    // Navigation
    'nav.dashboard': 'Tableau de bord',
    'nav.clients': 'Clients',
    'nav.appointments': 'Rendez-vous',
    'nav.prospecting': 'Prospection',
    'nav.users': 'Utilisateurs',
    'nav.signOut': 'Déconnexion',
    
    // Common
    'common.loading': 'Chargement...',
    'common.error': 'Erreur',
    'common.success': 'Succès',
    'common.cancel': 'Annuler',
    'common.save': 'Enregistrer',
    'common.edit': 'Modifier',
    'common.delete': 'Supprimer',
    'common.search': 'Rechercher',
    'common.filter': 'Filtrer',
    'common.sort': 'Trier',
    'common.actions': 'Actions',
    'common.status': 'Statut',
    'common.date': 'Date',
    'common.time': 'Heure',
    'common.name': 'Nom',
    'common.email': 'Email',
    'common.phone': 'Téléphone',
    'common.company': 'Entreprise',
    'common.notes': 'Notes',
    'common.description': 'Description',
    'common.location': 'Lieu',
    'common.title': 'Titre',
    'common.client': 'Client',
    'common.prospect': 'Prospect',
    'common.appointment': 'Rendez-vous',
    'common.appointments': 'Rendez-vous',
    'common.prospecting': 'Prospection',
    'common.user': 'Utilisateur',
    'common.users': 'Utilisateurs',
    'common.role': 'Rôle',
    'common.created': 'Créé',
    'common.updated': 'Modifié',
    'common.total': 'Total',
    'common.view': 'Voir',
    'common.details': 'Détails',
    'common.back': 'Retour',
    'common.next': 'Suivant',
    'common.previous': 'Précédent',
    'common.close': 'Fermer',
    'common.open': 'Ouvrir',
    'common.yes': 'Oui',
    'common.no': 'Non',
    'common.confirm': 'Confirmer',
    'common.warning': 'Attention',
    'common.info': 'Information',
    'common.help': 'Aide',
    'common.settings': 'Paramètres',
    'common.profile': 'Profil',
    'common.account': 'Compte',
    'common.language': 'Langue',
    'common.french': 'Français',
    'common.english': 'Anglais',
  },
  en: {
    // Login page
    'login.title': 'Sign in to Hook Lab',
    'login.subtitle': 'Access your prospecting dashboard',
    'login.email': 'Email address',
    'login.password': 'Password',
    'login.rememberMe': 'Remember me',
    'login.forgotPassword': 'Forgot password?',
    'login.signIn': 'Sign in',
    'login.signingIn': 'Signing in...',
    'login.invalidCredentials': 'Invalid email or password',
    'login.emailRequired': 'Email is required',
    'login.passwordRequired': 'Password is required',
    'login.invalidEmail': 'Please enter a valid email address',
    'login.passwordMinLength': 'Password must be at least 6 characters',
    
    // Navigation
    'nav.dashboard': 'Dashboard',
    'nav.clients': 'Clients',
    'nav.appointments': 'Appointments',
    'nav.prospecting': 'Prospecting',
    'nav.users': 'Users',
    'nav.signOut': 'Sign Out',
    
    // Dashboard
    'dashboard.subtitle': 'Overview of your Hook Lab activity',
    'dashboard.totalCalls': 'Contacts Made',
    'dashboard.totalAppointments': 'Total Appointments',
    'dashboard.totalProspectingDays': 'Prospecting Days',
    'dashboard.globalConversionRate': 'Conversion Rate',
    'dashboard.latestProspectingSessions': 'Latest Prospecting Sessions',
    'dashboard.recentAppointments': 'Recent Appointments',
    'dashboard.noProspectingData': 'No prospecting sessions found',
    'dashboard.noAppointmentData': 'No appointments found',
    'dashboard.filterByDateRange': 'Data filtered from {start} to {end}',
    
    // Common
    'common.loading': 'Loading...',
    'common.error': 'Error',
    'common.success': 'Success',
    'common.cancel': 'Cancel',
    'common.save': 'Save',
    'common.edit': 'Edit',
    'common.delete': 'Delete',
    'common.search': 'Search',
    'common.filter': 'Filter',
    'common.sort': 'Sort',
    'common.actions': 'Actions',
    'common.status': 'Status',
    'common.date': 'Date',
    'common.time': 'Time',
    'common.name': 'Name',
    'common.email': 'Email',
    'common.phone': 'Phone',
    'common.company': 'Company',
    'common.notes': 'Notes',
    'common.description': 'Description',
    'common.location': 'Location',
    'common.title': 'Title',
    'common.client': 'Client',
    'common.prospect': 'Prospect',
    'common.appointment': 'Appointment',
    'common.appointments': 'Appointments',
    'common.prospecting': 'Prospecting',
    'common.user': 'User',
    'common.users': 'Users',
    'common.role': 'Role',
    'common.created': 'Created',
    'common.updated': 'Updated',
    'common.total': 'Total',
    'common.view': 'View',
    'common.details': 'Details',
    'common.back': 'Back',
    'common.next': 'Next',
    'common.previous': 'Previous',
    'common.close': 'Close',
    'common.open': 'Open',
    'common.yes': 'Yes',
    'common.no': 'No',
    'common.confirm': 'Confirm',
    'common.warning': 'Warning',
    'common.info': 'Information',
    'common.help': 'Help',
    'common.settings': 'Settings',
    'common.profile': 'Profile',
    'common.account': 'Account',
    'common.language': 'Language',
    'common.french': 'French',
    'common.english': 'English',
  }
};

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<'fr' | 'en'>('fr');

  useEffect(() => {
    // Load language preference from localStorage
    const savedLanguage = localStorage.getItem('hooklab-language') as 'fr' | 'en';
    if (savedLanguage && (savedLanguage === 'fr' || savedLanguage === 'en')) {
      setLanguageState(savedLanguage);
    } else {
      // Default to French or detect browser language
      const browserLang = navigator.language.toLowerCase();
      const defaultLang = browserLang.startsWith('fr') ? 'fr' : 'en';
      setLanguageState(defaultLang);
    }
  }, []);

  const setLanguage = (lang: 'fr' | 'en') => {
    setLanguageState(lang);
    localStorage.setItem('hooklab-language', lang);
  };

  const t = (key: string): string => {
    return translations[language][key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};